import type React from 'react';
import type { Router } from '@remix-run/router';
import { RouterProvider } from 'react-router-dom';

import { createRouter, Navigate } from '@/js/libs/router';
import { not, or, PrivateRoute, Roles } from '@/js/libs/use-auth';

import 'src/api';
import { Layout } from './components/Layout';
import RoleBasedRedirector, { type Condition } from './components/RoleBasedRedirector';
import Providers from './Providers';
import {
  accountRouteObject,
  adminRouteObject,
  adTagsRouteObject,
  analogOperationsRouteObject,
  analyticsToolObject,
  placementConfigsRouteObject,
  businessIntelligenceRouteObject,
  complianceRouteObject,
  creativesRouteObject,
  crmRouteObject,
  dashboardRouteObject,
  digitalOperationsRouteObject,
  financesRouteObject,
  genAIObject,
  headerTagRouteObject,
  notificationsRouteObject,
  orders1RouteObject,
  orders2RouteObject,
  ordersPostCampaignsRouteObject,
  printOperationsRouteObject,
  printOperationsRouteObjectNew,
  publishersRouteObject,
  salesRouteObject,
  selfBookingRouteObject,
  semantiqRouteObject,
  settingsRouteObject,
  supportCenterRouteObject,
  targetingRouteObject,
  yaleoRouteObject,
} from './routes';
import { postCrmRouteObject } from './routes/postCrm';
import Authentication from './scenes/Authentication';
import Campaign from './scenes/Campaign';
import Mail, { PromotionMailSearchParams } from './scenes/Mail';
import Status from './scenes/Status';

const homeConditions: Condition[] = [
  { roles: Roles.AGENCY, to: '/orders' },
  { roles: Roles.YALEO_SELF_BOOKING, to: '/self-booking/campaigns' },
  { roles: Roles.POSTADMANAGER, to: '/orders/post-campaigns' },
  { roles: Roles.PUBLISHER, to: '/publishers' },
  { roles: or(Roles.SALESFUNNEL, Roles.ADMIN), to: '/sales' },
  { roles: Roles.CONTEXTUAL, to: '/targeting/keywords' },
  { roles: Roles.SEMANTIQ, to: '/semantiq' },
];

const index = true;

export const ROUTER = createRouter([
  {
    path: '/',
    element: <Providers />,
    children: [
      {
        element: <PrivateRoute roles={Roles.ANONYMOUS} redirectPath='/' />,
        children: [
          { path: 'login', element: <Navigate to='/authentication' /> },
          {
            path: 'authentication',
            children: [
              { index, Component: Authentication.Components.Redirector },
              {
                path: 'sso',
                children: [
                  { index, Component: Authentication.Components.Redirector },
                  { path: ':issuerName', element: <Authentication.Components.SSO /> },
                ],
              },
              { path: 'autologin', Component: Authentication.Components.Autologin },
              {
                Component: Authentication.Components.Layout,
                children: [
                  { path: 'login', Component: Authentication.Forms.Login },
                  { path: 'verification', Component: Authentication.Forms.Verification },
                  { path: 'restore-password', Component: Authentication.Forms.RestorePassword },
                  { path: 'change-password/:hash', Component: Authentication.Forms.ChangePassword },
                  { path: 'activate/:hash', Component: Authentication.Forms.ActivateUser },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <Layout />,
        children: [
          accountRouteObject,
          {
            element: <PrivateRoute roles={not(Roles.ANONYMOUS)} redirectPath='/login' />,
            children: [
              { index, element: <RoleBasedRedirector conditions={homeConditions} noAccessElement={<Status.NotFound />} /> },
              {
                path: 'mail',
                Component: Mail.Helmet,
                children: [
                  {
                    element: <PrivateRoute roles={Roles.SALESFUNNEL} noAccessComponent={Status.NotFound} />,
                    children: [
                      { path: 'promotion/:promotionId', Component: Mail.PromotionMail, searchParams: PromotionMailSearchParams.parse },
                    ],
                  },
                ],
              },
              notificationsRouteObject,
              {
                element: (
                  <PrivateRoute
                    roles={or(
                      Roles.AGENCY,
                      Roles.ADMIN,
                      Roles.POSTADMANAGER,
                      Roles.MANAGE_DIGITAL_PRODUCTS,
                      Roles.MANAGE_RADIO_PRODUCTS,
                      Roles.MANAGE_TV_PRODUCTS,
                      Roles.MANAGE_PRINT_PRODUCTS,
                      Roles.MANAGE_POS_PRODUCTS,
                    )}
                    noAccessComponent={Status.NotFound}
                  />
                ),
                children: [
                  {
                    Component: Campaign.FiltersProvider,
                    children: [orders1RouteObject, crmRouteObject],
                  },
                ],
              },
              postCrmRouteObject,
              ordersPostCampaignsRouteObject,
              dashboardRouteObject,
              orders2RouteObject,
              creativesRouteObject,
              businessIntelligenceRouteObject,
              semantiqRouteObject,
              publishersRouteObject,
              adTagsRouteObject,
              headerTagRouteObject,
              yaleoRouteObject,
              selfBookingRouteObject,
              printOperationsRouteObject,
              digitalOperationsRouteObject,
              analogOperationsRouteObject,
              financesRouteObject,
              settingsRouteObject,
              salesRouteObject,
              adminRouteObject,
              complianceRouteObject,
              targetingRouteObject,
              genAIObject,
              placementConfigsRouteObject,
              analyticsToolObject,
              printOperationsRouteObjectNew,
              supportCenterRouteObject,
              { path: '*', Component: Status.NotFound },
            ],
          },
          { path: '403', Component: Status.Forbidden },
        ],
      },
    ],
  },
]) satisfies Router;

export const AppRouter = ({ fallback }: { fallback: React.ReactNode }) => <RouterProvider router={ROUTER} fallbackElement={fallback} />;
